import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Typography } from "@mui/material";
import dashboardApi from "Services/Dashboard";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import {
  GridView,
  Equalizer,
  Savings,
  NotInterested,
  CarRepair,
  Hail,
  SensorOccupied,
  SupportAgent,
  Link,
  Schedule,
  ContactPhone,
  PunchClock,
  Close,
  KeyboardArrowUpRounded,
  KeyboardArrowDownRounded,
  RemoveRounded,
  FileDownloadOutlined,
} from "@mui/icons-material";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment-timezone";
import { CUSTOM_CONSTANTS } from "Constants";

const Dashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [dashboardDetails, setDashboardDetails] = useState(null);
  const [startDate, setStartDate] = useState({});
  const [endDate, setEndDate] = useState({});

  const getDashbaordDetails = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.status === 200) {
        setDashboardDetails(res?.data?.data);
      } else {
        setDashboardDetails([]);
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    dashboardApi.getDashboardDetails({ startDate, endDate }).then(onSuccess, onFailure);
  }, [dispatch, startDate, endDate]);

  useEffect(() => {
    getDashbaordDetails();
  }, [getDashbaordDetails]);

  const handlefromdate = (newValue) => {
    const selectedDate = newValue instanceof Date ? newValue : new Date(newValue);
    const parsedDate = moment(selectedDate);
    const formattedDate = parsedDate.format("MM/DD/YYYY HH:mm:ss");
    setStartDate(formattedDate);
  };

  const handletodate = (newValue) => {
    const selectedDate = newValue instanceof Date ? newValue : new Date(newValue);
    const parsedDate = moment(selectedDate);
    const formattedDate = parsedDate.format("MM/DD/YYYY HH:mm:ss");
    setEndDate(formattedDate);
  };

  const metricCards = [
    {
      label: "Total No. of Rides",
      value: dashboardDetails?.totalRidesCount || 0,
      trend: "12.5%",
      trendTone: "up",
      Icon: Equalizer,
      tone: "green",
      waveTone: "greenWave",
    },
    {
      label: "Revenue",
      value: `${dashboardDetails?.totalRevenue || 0} ${CUSTOM_CONSTANTS.BASE_CURRENCY}`,
      trend: "18.7%",
      trendTone: "up",
      Icon: Savings,
      tone: "green",
      waveTone: "greenWave",
    },
    {
      label: "Total Kilometer Driven",
      value: `${Number(dashboardDetails?.driverkilometerdriven || 0).toFixed(2)} KM`,
      trend: "10.3%",
      trendTone: "up",
      Icon: SensorOccupied,
      tone: "green",
      waveTone: "greenWave",
    },
    {
      label: "Total Minutes Spent",
      value: `${Number(dashboardDetails?.total_minutes || 0).toFixed(2)} Mins`,
      trend: "8.6%",
      trendTone: "up",
      Icon: PunchClock,
      tone: "green",
      waveTone: "greenWave",
    },
    {
      label: "Total Cancelled Rides",
      value: dashboardDetails?.cancelledRides || 0,
      trend: "4.2%",
      trendTone: "down",
      Icon: NotInterested,
      tone: "red",
      waveTone: "redWave",
    },
    {
      label: "No. of Service Types",
      value: dashboardDetails?.noOfServiceTypes || 0,
      trend: "0%",
      trendTone: "flat",
      Icon: CarRepair,
      tone: "blue",
      waveTone: "blueWave",
    },
    {
      label: "User Cancelled",
      value: dashboardDetails?.userCancelledRidesCount || 0,
      trend: "2.1%",
      trendTone: "down",
      Icon: Hail,
      tone: "amber",
      waveTone: "amberWave",
    },
    {
      label: "Dispatcher Cancelled",
      value: dashboardDetails?.dispatcherCancelledRidesCount || 0,
      trend: "1.8%",
      trendTone: "down",
      Icon: SupportAgent,
      tone: "purple",
      waveTone: "purpleWave",
    },
    {
      label: "No. of Carrier",
      value: dashboardDetails?.noOfCarrier || 0,
      trend: "5.3%",
      trendTone: "up",
      Icon: Link,
      tone: "green",
      waveTone: "greenWave",
    },
    {
      label: "No. of Scheduled Rides",
      value: dashboardDetails?.scheduledRidesCount || 0,
      trend: "15.6%",
      trendTone: "up",
      Icon: Schedule,
      tone: "purple",
      waveTone: "purpleWave",
    },
    {
      label: "No. of Dispatcher Rides",
      value: dashboardDetails?.dispatcherRidesCount || 0,
      trend: "9.4%",
      trendTone: "up",
      Icon: ContactPhone,
      tone: "blue",
      waveTone: "blueWave",
    },
    {
      label: "Not Accepted by Driver",
      value: dashboardDetails?.driverNotAcceptedCount || 0,
      trend: "3.7%",
      trendTone: "down",
      Icon: Close,
      tone: "orange",
      waveTone: "orangeWave",
    },
  ];

  return (
    <section className={classes.dashboardPage}>
      <div className={classes.pageDecor} />
      <div className={classes.dashboardTopbar}>
        <div className={classes.pageIntro}>
          <div className={classes.pageTitleWrap}>
            <GridView className={classes.titleIcon} />
            <Typography variant="h3">Dashboard</Typography>
          </div>
          <Typography variant="body2">Welcome back! Here&apos;s what&apos;s happening today.</Typography>
        </div>

        <div className={classes.filters}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From date"
              value={startDate}
              onChange={(newValue) => {
                handlefromdate(newValue);
              }}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="To date"
              value={endDate}
              onChange={(newValue) => {
                handletodate(newValue);
              }}
              renderInput={(params) => <TextField size="small" {...params} />}
            />
          </LocalizationProvider>

          <Button className={classes.exportButton} variant="contained" startIcon={<FileDownloadOutlined />}>
            EXPORT REPORT
          </Button>
        </div>
      </div>

      <Grid container className={classes.root} spacing={3}>
        {metricCards.map(({ label, value, trend, trendTone, Icon, tone, waveTone }, index) => {
          const TrendIcon =
            trendTone === "down" ? KeyboardArrowDownRounded : trendTone === "up" ? KeyboardArrowUpRounded : RemoveRounded;

          return (
          <Grid item xs={12} sm={6} md={3} key={`${label}-${index}`}>
            <section className={`${classes.card} ${classes[waveTone]} ${index > 3 ? classes.compactCard : ""}`}>
              <div className={classes.cardContent}>
                <span className={`${classes.iconBadge} ${classes[tone]}`}>
                  <Icon />
                </span>
                <div>
                  <div className={classes.nameLabel}>
                    <Typography variant="h5">{label}</Typography>
                  </div>
                  <div className={classes.count}>
                    <Typography variant="h4">{value}</Typography>
                  </div>
                  <div
                    className={`${classes.trend} ${classes[trendTone]} ${
                      trendTone === "flat" ? classes.flatTag : ""
                    }`}
                  >
                    <TrendIcon />
                    {trend} <em>from last month</em>
                  </div>
                </div>
              </div>
            </section>
          </Grid>
        );
        })}
      </Grid>
    </section>
  );
};

export default Dashboard;
